import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Search, Edit3, Trash2, CheckCircle, XCircle, Clock,
  RefreshCw, Package, DollarSign, Loader2, X, ShieldCheck,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const APPROVAL_COLORS = {
  pending_review: 'bg-amber-100 text-amber-800 border-amber-300',
  approved: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  discontinued: 'bg-slate-200 text-slate-600 border-slate-300',
  replacement_candidate: 'bg-rose-100 text-rose-800 border-rose-300',
};

const CATEGORY_LABELS = {
  books_take_home_readers: 'Books / Take-Home Readers',
  consumables: 'Consumables',
  student_practice_materials: 'Student Practice Materials',
  parent_family_engagement: 'Parent & Family Engagement',
  teacher_resources: 'Teacher Resources',
  classroom_support_materials: 'Classroom Support Materials',
  professional_learning: 'Professional Learning',
  digital_add_ons: 'Digital Add-Ons',
};

const EMPTY_FORM = {
  sku: '', name: '', category: 'books_take_home_readers',
  product_type: 'individual', grade_band: 'K', audience: 'ell_general',
  instructional_purpose: '',
  consumable: false, reorderable: false, replenishment_cycle: '',
  vendor: '', vendor_sku: '',
  acquisition_cost: 0, markup_pct: 25, shipping_mode: 'included', shipping_cost: 0,
  fulfillment_mode: 'drop_ship_vendor',
  active: true, quote_eligible: true, mosaic_pairable: true,
  inventory_status: 'available', substitute_allowed: false,
  customer_description: '', internal_notes: '',
};

export const SupplementalSolutionsTab = () => {
  const [vocab, setVocab] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ category: '', grade_band: '', approval_status: '' });
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // sku or null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('session_token')}` } });

  const loadVocab = async () => {
    try {
      const r = await axios.get(`${API}/api/supplemental-solutions/vocab`);
      setVocab(r.data);
    } catch { /* vocab is public — non-fatal */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.category) params.set('category', filter.category);
      if (filter.grade_band) params.set('grade_band', filter.grade_band);
      if (filter.approval_status) params.set('approval_status', filter.approval_status);
      const r = await axios.get(`${API}/api/supplemental-solutions/products?${params}`, H());
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    }
    setLoading(false);
  };

  useEffect(() => { loadVocab(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter.category, filter.grade_band, filter.approval_status]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.sku || '').toLowerCase().includes(q) ||
      (it.name || '').toLowerCase().includes(q) ||
      (it.vendor || '').toLowerCase().includes(q)
    );
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (it) => {
    setEditing(it.sku);
    setForm({
      ...EMPTY_FORM,
      ...it,
      acquisition_cost: Number(it.acquisition_cost || 0),
      markup_pct: Number(it.markup_pct || 0),
      shipping_cost: Number(it.shipping_cost || 0),
      replenishment_cycle: it.replenishment_cycle || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.sku || !form.name) {
      toast.error('SKU and Name are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const { sku, ...body } = form;
        void sku;
        await axios.patch(
          `${API}/api/supplemental-solutions/products/${editing}`, body, H()
        );
        toast.success('Updated');
      } else {
        await axios.post(`${API}/api/supplemental-solutions/products`, form, H());
        toast.success('Created — status: pending_review');
      }
      setShowModal(false);
      setForm(EMPTY_FORM);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  const transition = async (sku, to_state, reason = '') => {
    try {
      await axios.post(
        `${API}/api/supplemental-solutions/products/${sku}/approval`,
        { to_state, reason }, H()
      );
      toast.success(`→ ${to_state}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Transition failed');
    }
  };

  const softDelete = async (sku) => {
    if (!window.confirm(`Soft-delete ${sku}? It will be moved to 'discontinued' and hidden from quotes. History is preserved.`)) return;
    try {
      await axios.delete(`${API}/api/supplemental-solutions/products/${sku}`, H());
      toast.success('Discontinued');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const sellingPrice = useMemo(() => {
    const c = Number(form.acquisition_cost || 0);
    const m = Number(form.markup_pct || 0);
    return (c * (1 + m / 100)).toFixed(2);
  }, [form.acquisition_cost, form.markup_pct]);

  return (
    <div className="space-y-4" data-testid="supplemental-solutions-tab">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Package size={20} className="text-indigo-700" />
            Supplemental Solutions
          </h2>
          <p className="text-sm text-slate-600">
            Controlled catalog of products that can be paired with MOSAIC. Approved items appear in the quote generator.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ss-add-btn">
          <Plus size={16} className="mr-2" /> Add Product
        </Button>
      </div>

      {/* Hard boundary notice */}
      <Card className="p-3 border-l-4 border-l-amber-500 bg-amber-50" data-testid="ss-boundary-notice">
        <div className="flex gap-2 items-start text-xs text-amber-900">
          <ShieldCheck size={16} className="shrink-0 mt-0.5" />
          <div>
            <b>Architectural boundary:</b> Supplemental products never modify MOSAIC assessment content, scoring,
            exams, or BLM generation. They can be quoted alongside MOSAIC and recommended by grade band — nothing more.
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-3">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <div>
            <label className="text-xs uppercase text-slate-500">Search</label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-2 top-2.5 text-slate-400" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="SKU / name / vendor"
                className="pl-7 h-9"
                data-testid="ss-search"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500">Category</label>
            <select
              value={filter.category}
              onChange={e => setFilter({ ...filter, category: e.target.value })}
              className="mt-1 w-full h-9 border rounded px-2 text-sm"
              data-testid="ss-filter-category"
            >
              <option value="">All</option>
              {(vocab?.categories || []).map(c => (
                <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500">Grade band</label>
            <select
              value={filter.grade_band}
              onChange={e => setFilter({ ...filter, grade_band: e.target.value })}
              className="mt-1 w-full h-9 border rounded px-2 text-sm"
              data-testid="ss-filter-grade"
            >
              <option value="">All</option>
              {(vocab?.grade_bands || []).map(g => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase text-slate-500">Approval</label>
            <select
              value={filter.approval_status}
              onChange={e => setFilter({ ...filter, approval_status: e.target.value })}
              className="mt-1 w-full h-9 border rounded px-2 text-sm"
              data-testid="ss-filter-approval"
            >
              <option value="">All</option>
              {(vocab?.approval_states || []).map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={load} className="w-full h-9" data-testid="ss-refresh">
              <RefreshCw size={14} className="mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </Card>

      {/* List */}
      {loading ? (
        <div className="text-center text-slate-400 py-8">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-slate-500" data-testid="ss-empty">
          <Package size={48} className="mx-auto text-slate-300 mb-2" />
          <div className="font-semibold text-slate-700">No supplemental products yet</div>
          <div className="text-sm mt-1">
            Click <b>Add Product</b> to seed your first item. Products start in <i>pending_review</i>.
          </div>
        </Card>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="ss-table">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Grade</th>
                <th className="text-right px-3 py-2">Cost</th>
                <th className="text-right px-3 py-2">Markup</th>
                <th className="text-right px-3 py-2">Selling</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => (
                <tr key={it.sku} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ss-row-${it.sku}`}>
                  <td className="px-3 py-2 font-mono text-xs">{it.sku}</td>
                  <td className="px-3 py-2 font-medium">
                    {it.name}
                    <div className="flex gap-1 mt-1 flex-wrap">
                      {it.consumable && <Badge className="bg-cyan-100 text-cyan-800 text-[10px]">Consumable</Badge>}
                      {it.reorderable && <Badge className="bg-blue-100 text-blue-800 text-[10px]">Reorderable</Badge>}
                      {it.mosaic_pairable && <Badge className="bg-purple-100 text-purple-800 text-[10px]">MOSAIC-Pairable</Badge>}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{CATEGORY_LABELS[it.category] || it.category}</td>
                  <td className="px-3 py-2 text-xs">{it.grade_band}</td>
                  <td className="px-3 py-2 text-right text-xs">${Number(it.acquisition_cost || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right text-xs">{it.markup_pct}%</td>
                  <td className="px-3 py-2 text-right font-semibold">${Number(it.selling_price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2">
                    <Badge className={`text-[10px] border ${APPROVAL_COLORS[it.approval_status] || ''}`}>
                      {(it.approval_status || 'pending_review').replace(/_/g, ' ')}
                    </Badge>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      {it.approval_status !== 'approved' && it.approval_status !== 'discontinued' && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => transition(it.sku, 'approved')}
                          title="Approve" className="text-emerald-700 h-7 px-2"
                          data-testid={`ss-approve-${it.sku}`}
                        >
                          <CheckCircle size={12} />
                        </Button>
                      )}
                      {it.approval_status === 'approved' && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => transition(it.sku, 'pending_review', 'reverted for review')}
                          title="Send back to pending" className="text-amber-700 h-7 px-2"
                          data-testid={`ss-unapprove-${it.sku}`}
                        >
                          <Clock size={12} />
                        </Button>
                      )}
                      <Button
                        size="sm" variant="outline"
                        onClick={() => openEdit(it)}
                        title="Edit" className="h-7 px-2"
                        data-testid={`ss-edit-${it.sku}`}
                      >
                        <Edit3 size={12} />
                      </Button>
                      <Button
                        size="sm" variant="outline"
                        onClick={() => softDelete(it.sku)}
                        title="Discontinue" className="text-rose-600 h-7 px-2"
                        data-testid={`ss-delete-${it.sku}`}
                      >
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => !saving && setShowModal(false)}>
          <Card
            className="w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
            onClick={e => e.stopPropagation()}
            data-testid="ss-modal"
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {editing ? `Edit ${editing}` : 'Add Supplemental Product'}
                </h3>
                {!editing && (
                  <p className="text-xs text-slate-500 mt-1">
                    New products start as <b>pending_review</b>. Approve once ready to sell.
                  </p>
                )}
              </div>
              <button onClick={() => setShowModal(false)} className="text-slate-500" disabled={saving}>
                <X size={18} />
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <Field label="SKU *" testid="ss-form-sku">
                <Input value={form.sku} disabled={!!editing} onChange={e => setForm({ ...form, sku: e.target.value.toUpperCase() })} placeholder="MOS-SUPP-K-NCLIT-01" />
              </Field>
              <Field label="Name *" testid="ss-form-name">
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="First Words & Family Connections" />
              </Field>

              <Field label="Category *" testid="ss-form-category">
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  {(vocab?.categories || []).map(c => (
                    <option key={c} value={c}>{CATEGORY_LABELS[c] || c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Product Type" testid="ss-form-type">
                <select value={form.product_type} onChange={e => setForm({ ...form, product_type: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  {(vocab?.product_types || []).map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>

              <Field label="Grade Band *" testid="ss-form-grade">
                <select value={form.grade_band} onChange={e => setForm({ ...form, grade_band: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  {(vocab?.grade_bands || []).map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </Field>
              <Field label="Audience" testid="ss-form-audience">
                <select value={form.audience} onChange={e => setForm({ ...form, audience: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  {(vocab?.audiences || []).map(a => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
                </select>
              </Field>

              <div className="md:col-span-2">
                <Field label="Instructional Purpose (comma-separated tags)" testid="ss-form-purpose">
                  <Input value={form.instructional_purpose} onChange={e => setForm({ ...form, instructional_purpose: e.target.value })} placeholder="Reading, Vocabulary, Family Engagement" />
                </Field>
              </div>

              <Field label="Vendor" testid="ss-form-vendor">
                <Input value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} placeholder="Amazon / LERNO / …" />
              </Field>
              <Field label="Vendor SKU / ISBN" testid="ss-form-vendor-sku">
                <Input value={form.vendor_sku} onChange={e => setForm({ ...form, vendor_sku: e.target.value })} placeholder="978-1234567890" />
              </Field>

              <Field label={<><DollarSign size={12} className="inline" /> Acquisition Cost *</>} testid="ss-form-cost">
                <Input type="number" step="0.01" value={form.acquisition_cost} onChange={e => setForm({ ...form, acquisition_cost: e.target.value })} />
              </Field>
              <Field label="Markup % (default 25)" testid="ss-form-markup">
                <Input type="number" step="0.1" value={form.markup_pct} onChange={e => setForm({ ...form, markup_pct: e.target.value })} />
              </Field>

              <div className="md:col-span-2">
                <div className="p-2 bg-slate-50 border border-slate-200 rounded text-sm flex justify-between items-center">
                  <span className="text-slate-600">Computed selling price:</span>
                  <span className="font-bold text-slate-900" data-testid="ss-form-selling-preview">${sellingPrice}</span>
                </div>
              </div>

              <Field label="Shipping" testid="ss-form-shipping-mode">
                <select value={form.shipping_mode} onChange={e => setForm({ ...form, shipping_mode: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  <option value="included">Included</option>
                  <option value="additional">Additional</option>
                </select>
              </Field>
              <Field label="Shipping Cost" testid="ss-form-shipping-cost">
                <Input type="number" step="0.01" value={form.shipping_cost} onChange={e => setForm({ ...form, shipping_cost: e.target.value })} disabled={form.shipping_mode === 'included'} />
              </Field>

              <Field label="Fulfillment Mode" testid="ss-form-fulfillment">
                <select value={form.fulfillment_mode} onChange={e => setForm({ ...form, fulfillment_mode: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  {(vocab?.fulfillment_modes || []).map(m => <option key={m} value={m}>{m.replace(/_/g, ' ')}</option>)}
                </select>
              </Field>
              <Field label="Inventory" testid="ss-form-inventory">
                <select value={form.inventory_status} onChange={e => setForm({ ...form, inventory_status: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                  {(vocab?.inventory_states || []).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </Field>

              <div className="md:col-span-2 flex flex-wrap gap-4 pt-2 border-t border-slate-200">
                <Toggle label="Consumable" value={form.consumable} onChange={v => setForm({ ...form, consumable: v })} testid="ss-form-consumable" />
                <Toggle label="Reorderable" value={form.reorderable} onChange={v => setForm({ ...form, reorderable: v })} testid="ss-form-reorderable" />
                <Toggle label="Active" value={form.active} onChange={v => setForm({ ...form, active: v })} testid="ss-form-active" />
                <Toggle label="Quote Eligible" value={form.quote_eligible} onChange={v => setForm({ ...form, quote_eligible: v })} testid="ss-form-quote-eligible" />
                <Toggle label="MOSAIC Pairable" value={form.mosaic_pairable} onChange={v => setForm({ ...form, mosaic_pairable: v })} testid="ss-form-pairable" />
                <Toggle label="Substitute Allowed" value={form.substitute_allowed} onChange={v => setForm({ ...form, substitute_allowed: v })} testid="ss-form-substitute" />
              </div>

              {form.consumable && (
                <Field label="Replenishment Cycle" testid="ss-form-replenishment">
                  <select value={form.replenishment_cycle || ''} onChange={e => setForm({ ...form, replenishment_cycle: e.target.value })} className="w-full border rounded px-2 py-2 text-sm">
                    <option value="">— none —</option>
                    <option value="annual">Annual</option>
                    <option value="semester">Semester</option>
                    <option value="quarterly">Quarterly</option>
                  </select>
                </Field>
              )}

              <div className="md:col-span-2">
                <Field label="Customer Description (quote-ready copy)" testid="ss-form-customer-desc">
                  <Textarea rows={2} value={form.customer_description} onChange={e => setForm({ ...form, customer_description: e.target.value })} />
                </Field>
              </div>
              <div className="md:col-span-2">
                <Field label="Internal Notes (procurement only — never shown to customer)" testid="ss-form-notes">
                  <Textarea rows={2} value={form.internal_notes} onChange={e => setForm({ ...form, internal_notes: e.target.value })} />
                </Field>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-slate-200">
              <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ss-form-save">
                {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
                {editing ? 'Save Changes' : 'Create Product'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <label className="text-xs uppercase text-slate-500 block mb-1">{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, value, onChange, testid }) => (
  <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid={testid}>
    <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} className="rounded" />
    <span className="text-slate-700">{label}</span>
  </label>
);

export default SupplementalSolutionsTab;
